import type {
  GraphQLCommitment,
  GraphQLDeparture,
  GraphQLGivingTotals,
  GraphQLLetter,
  GraphQLSupporter,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  GraphQLDepartureExitType,
  GraphQLLetterDirection,
  GraphQLLetterFriendlyStatus,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { MOCK_DEPARTURE_COMMITMENTS } from './commitments.ts';
import { MOCK_SUPPORTER_CONTACTS } from './contacts.ts';
import { MOCK_SUPPORTER_TRANSACTIONS } from './transactions.ts';
import { MOCK_SUPPORTER_VOLUNTEER_EVENTS } from './volunteerEvents.ts';

export const MOCK_SUPPORTER_LETTERS_AVAILABLE: GraphQLLetter[] = [
  {
    id: 'letter-available-1',
    sourceLetterId: 'source-letter-available-1',
    status: GraphQLLetterFriendlyStatus.Complete,
    direction: GraphQLLetterDirection.B2S,
    createdDate: '2026-07-04T14:30:00.000Z',
    readyDate: '2026-07-18T09:15:00.000Z',
    isFinalLetter: true,
    isReadBySupporter: true,
    letterUrl: 'https://example.com/letters/letter-available-1',
    numberOfPages: 2,
    pages: [
      { url: 'https://example.com/letters/letter-available-1?format=jpg&pg=1' },
      { url: 'https://example.com/letters/letter-available-1?format=jpg&pg=2' },
    ],
    beneficiary: {
      globalId: '08314403',
      name: 'Luciana',
    },
    supporter: {
      globalId: '09090785',
      preferredName: 'Charlie',
    },
  },
];

export const MOCK_SUPPORTER_LETTERS_IN_TRANSIT: GraphQLLetter[] = [
  {
    id: 'letter-in-transit-1',
    sourceLetterId: 'source-letter-in-transit-1',
    status: GraphQLLetterFriendlyStatus.InTranslation,
    direction: GraphQLLetterDirection.S2B,
    createdDate: '2026-07-10T16:45:00.000Z',
    readyDate: null,
    isFinalLetter: false,
    isReadBySupporter: false,
    letterUrl: 'https://example.com/letters/letter-in-transit-1',
    numberOfPages: 1,
    pages: [
      {
        url: 'https://example.com/letters/letter-in-transit-1?format=jpg&pg=1',
      },
    ],
    beneficiary: {
      globalId: '08314403',
      name: 'Luciana',
    },
    supporter: {
      globalId: '09090785',
      preferredName: 'Charlie',
    },
  },
];

export const MOCK_SUPPORTER_LETTERS_MIXED: GraphQLLetter[] = [
  {
    id: 'letter-mix-unread',
    status: GraphQLLetterFriendlyStatus.Complete,
    direction: GraphQLLetterDirection.B2S,
    createdDate: '2026-07-20T12:00:00.000Z',
    readyDate: '2026-07-27T12:00:00.000Z',
    isReadBySupporter: false,
    letterUrl: 'https://example.com/letters/letter-mix-unread',
    numberOfPages: 1,
    pages: [],
    beneficiary: { globalId: '08314403', name: 'Luciana' },
  },
  {
    id: 'letter-mix-outgoing',
    status: GraphQLLetterFriendlyStatus.InTranslation,
    direction: GraphQLLetterDirection.S2B,
    createdDate: '2026-07-26T12:00:00.000Z',
    numberOfPages: 1,
    pages: [],
    beneficiary: { globalId: '08314403', name: 'Luciana' },
  },
  {
    id: 'letter-mix-incoming',
    status: GraphQLLetterFriendlyStatus.InTranslation,
    direction: GraphQLLetterDirection.B2S,
    createdDate: '2026-07-25T12:00:00.000Z',
    numberOfPages: 1,
    pages: [],
    beneficiary: { globalId: '08741919', name: 'Jona' },
  },
  {
    id: 'letter-mix-problem',
    status: GraphQLLetterFriendlyStatus.HasAProblem,
    direction: GraphQLLetterDirection.S2B,
    createdDate: '2026-07-24T12:00:00.000Z',
    numberOfPages: 1,
    pages: [],
    beneficiary: { globalId: '08741919', name: 'Jona' },
  },
];

// NOTE Should these live in schema and be autogenerated when there are changes to the GraphQL schema?
export const MOCK_SUPPORTER_MIXED_COMMITMENTS: GraphQLSupporter = {
  dataStatus: {
    isPartial: false,
    issues: [],
  },
  addressDetails: {
    street: '1732 Gabriella Dr',
    street2: null,
    city: 'Oxnard',
    state: 'CA',
    postalCode: '93030-7667',
    country: 'United States',
    isUndeliverable: false,
  },
  commitments: [
    ...MOCK_DEPARTURE_COMMITMENTS,
    {
      participant: {
        birthdate: '2014-04-25',
        country: 'Colombia',
        gender: 'Female',
        globalId: '08314403',
        localId: 'CO075900062',
        name: 'Luciana',
        photoUrl:
          'https://media.ci.org/w_150/v1536346920/ChildPhotos/Published/08314403_iskbag.jpg',
      },
      commitmentType: 'GiftCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      dates: {
        start: '2020-02-10',
        end: '2020-08-26',
      },
      frequency: 'Annually',
      fund: {
        code: 'BG',
        description: 'Birthday Gift',
        icon: 'birthday-cake',
      },
      globalId: 'CN-11818661',
      internalId: 'a1o3b000006Lp9nAAC',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: false,
      lastCorrespondenceDate: null,
      paymentFrequency: null,
      paymentMethodId: null,
      paymentPullDay: null,
      rate: 20,
      amountDue: 15,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '593870',
      status: 'Active',
      transactionId: 'T-063514761',
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Gifts',
        recordType: 'Gifts',
        subProgram: null,
      },
    },
    {
      amountDue: 150,
      participant: {
        birthdate: '2014-04-25',
        country: 'Colombia',
        gender: 'Female',
        globalId: '08314403',
        localId: 'CO075900062',
        name: 'Luciana',
        photoUrl:
          'https://media.ci.org/w_150/v1536346920/ChildPhotos/Published/08314403_iskbag.jpg',
      },
      commitmentType: 'ChildSponsorship',
      canGiveGifts: true,
      canMakePayments: true,
      canWrite: true,
      dates: {
        start: '2019-11-20',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'DS',
        description: 'Child Sponsorship Support',
      },
      globalId: 'CN-11420555',
      internalId: 'a1o3b000006AHvqAAG',
      interventionId: null,
      isCorrespondent: true,
      isRecurring: true,
      lastCorrespondenceDate: '2020-02-06',
      paymentFrequency: 'Monthly',
      paymentMethodId: 'a2LOt000008s0BJMAY',
      paymentPullDay: 15,
      rate: 38,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '595241',
      status: 'Active',
      transactionId: 'BENORD0018623129',
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'CDSP',
        recordType: 'Ongoing Commitments',
        subProgram: 'Sponsor',
      },
    },
    {
      amountDue: 86,
      participant: {
        birthdate: '2014-12-22',
        country: 'Ethiopia',
        gender: 'Male',
        globalId: '08741919',
        localId: 'ET017600065',
        name: 'Jona',
        photoUrl:
          'https://media.ci.org/w_150/v1717045808/ChildPhotos/Published/08741919_fspnhs.jpg',
      },
      commitmentType: 'ChildSponsorship',
      canGiveGifts: true,
      canMakePayments: true,
      canWrite: true,
      dates: {
        start: '2019-12-11',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'DS',
        description: 'Child Sponsorship Support',
      },
      globalId: 'CN-11543478',
      internalId: 'a1o3b000006CWBSAA4',
      interventionId: null,
      isCorrespondent: true,
      isRecurring: true,
      lastCorrespondenceDate: '2025-10-27',
      paymentFrequency: 'Quarterly',
      paymentMethodId: 'a2LOt000008s0BJMAY',
      paymentPullDay: 6,
      rate: 43,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '127445',
      status: 'Active',
      transactionId: 'T-171474986',
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'CDSP',
        recordType: 'Ongoing Commitments',
        subProgram: 'Sponsor',
      },
    },
    {
      participant: null,
      commitmentType: 'GiftCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      dates: {
        start: '2025-09-08',
        end: null,
      },
      frequency: 'Annually',
      fund: {
        code: 'CF',
        description: 'Christmas Gift Program',
        icon: 'christmas-gift',
      },
      globalId: 'CN-21110207',
      internalId: 'a1oOt00000VCJzcIAH',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: true,
      lastCorrespondenceDate: null,
      paymentFrequency: 'Annually',
      paymentMethodId: 'a2LOt000005s0BJMAY',
      paymentPullDay: 8,
      rate: 150,
      amountDue: 100,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '15138',
      status: 'Active',
      transactionId: null,
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Funds',
        recordType: 'Donations',
        subProgram: null,
      },
    },
    {
      participant: null,
      commitmentType: 'FundCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      dates: {
        start: '2025-09-08',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'ER',
        description: 'Disaster Relief',
        icon: 'cloud-raining-03',
      },
      globalId: 'CN-21110204',
      internalId: 'a1oOt00000VCJzZIAX',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: true,
      lastCorrespondenceDate: null,
      paymentFrequency: 'Monthly',
      paymentMethodId: 'a2LOt000005s0BJMAY',
      paymentPullDay: 4,
      rate: 10,
      amountDue: 20,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '15138',
      status: 'Active',
      transactionId: null,
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Continuity Funds',
        recordType: 'Ongoing Commitments',
        subProgram: null,
      },
    },
    {
      participant: null,
      amountDue: 300,
      commitmentType: 'FundCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      dates: {
        start: '2025-09-08',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'MF',
        description: 'Survival Intervention',
        icon: 'mom-baby',
      },
      globalId: 'CN-21110205',
      internalId: 'a1oOt00000VCJzaIAH',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: true,
      lastCorrespondenceDate: null,
      paymentFrequency: 'Monthly',
      paymentMethodId: 'a2LOt000008s0BJMAY',
      paymentPullDay: 4,
      rate: 12,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '15138',
      status: 'Active',
      transactionId: null,
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Continuity Funds',
        recordType: 'Ongoing Commitments',
        subProgram: null,
      },
    },
    {
      participant: null,
      commitmentType: 'FundCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      amountDue: 180,
      dates: {
        start: '2025-09-08',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'UC',
        description: 'Unsponsored Children',
        icon: 'children',
      },
      globalId: 'CN-21110206',
      internalId: 'a1oOt00000VCJzbIAH',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: true,
      lastCorrespondenceDate: null,
      paymentFrequency: 'Monthly',
      paymentMethodId: 'a2LOt000008s0BJMAY',
      paymentPullDay: 4,
      rate: 9,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '15138',
      status: 'Active',
      transactionId: null,
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Continuity Funds',
        recordType: 'Ongoing Commitments',
        subProgram: null,
      },
    },
    {
      participant: null,
      commitmentType: 'FundCommitment',
      canGiveGifts: false,
      canMakePayments: true,
      canWrite: false,
      dates: {
        start: '2025-09-08',
        end: null,
      },
      frequency: 'Monthly',
      fund: {
        code: 'MN',
        description: 'Where Most Needed',
        icon: 'heart-hand',
      },
      globalId: 'CN-21110203',
      internalId: 'a1oOt00000VCJzYIAX',
      interventionId: null,
      isCorrespondent: null,
      isRecurring: true,
      amountDue: 244,
      lastCorrespondenceDate: null,
      paymentFrequency: 'Monthly',
      paymentMethodId: 'a2LOt000008s0BJMAY',
      paymentPullDay: 4,
      rate: 15,
      paymentDateNext: '2025-04-16',
      receiptorId: '09090785',
      sourceCode: '15138',
      status: 'Active',
      transactionId: null,
      _sys: {
        billingType: 'Billing',
        delinkType: null,
        linkType: 'New Assignment',
        program: 'Continuity Funds',
        recordType: 'Ongoing Commitments',
        subProgram: null,
      },
    },
  ],
  correspondenceDeliveryPreference:
    'Email of Letter Image and Printed Digital Document',
  email: 'charlie.fergison@gmail.com.invalid',
  firstName: 'Charlie',
  lastName: 'Fergison',
  salutation: 'Mr.',
  suffix: 'Jr.',
  gender: 'Male',
  phone: '(719) 555-1212',
  preferredName: 'Charlie',
  preferredUsername: 'charlie.fergison',
  primaryLanguage: 'English',
  undeliverableEmail: false,
  undeliverablePhone: false,
  globalId: '09090785',
  internalId: '0013b00002ABCdEAAT',
  givingTotals: {
    amountDue: 0,
    amountWaived: 0,
    annual: 150,
    monthly: 46,
    quarterly: 0,
    semiAnnual: 0,
    totalCreditRemaining: 0,
    totalDueThisMonthAmount: 0,
    totalMissedPaymentAmount: 0,
  },
  volunteerEvents: MOCK_SUPPORTER_VOLUNTEER_EVENTS,
  lettersAvailable: MOCK_SUPPORTER_LETTERS_MIXED.filter(
    ({ status }) => status === GraphQLLetterFriendlyStatus.Complete,
  ),
  lettersInTransit: MOCK_SUPPORTER_LETTERS_MIXED.filter(
    ({ status }) => status !== GraphQLLetterFriendlyStatus.Complete,
  ),
  recentTransactions: MOCK_SUPPORTER_TRANSACTIONS,
  displayName: 'Charlie',
  organizationName: null,
  isGroup: false,
  paymentMethods: [
    {
      id: 'a2LOt000008s0BJMAY',
      status: 'Active',
      accountNickname: 'Joint Account',
      sourceCode: null,
      isSavedForReuse: true,
      billingAddress: {
        attention: null,
        street: '1732 Gabriella Dr',
        city: 'Oxnard',
        stateCode: 'CA',
        postalCode: '93030-7667',
        country: 'United States',
      },
      paymentType: 'ACH',
      creditCard: null,
      ach: {
        accountName: 'Charlie Fergison',
        lastFourOfAccountNumber: '5312',
        routingNumber: '021200025',
        token: '3U37LV5UEYWOOA4G0',
      },
      isCreatedForReceiptor: true,
    },
    {
      id: 'a2LOt000008s0BJBAH',
      status: 'Active',
      accountNickname: 'My Visa 1',
      sourceCode: null,
      isSavedForReuse: true,
      billingAddress: {
        attention: null,
        street: '43222 San Jose Dr',
        city: 'Oxnard',
        stateCode: 'OR',
        postalCode: '52246-1133',
        country: 'United States',
      },
      paymentType: 'Credit Card',
      creditCard: {
        cardholderName: 'Charlie Fergison',
        cardType: 'Visa',
        digitalType: null,
        expirationDate: '12/2026',
        expiryStatus: null,
        lastFour: '2335',
        responseCode: '1234',
        token: '1234',
      },
      ach: null,
      isCreatedForReceiptor: true,
    },
    {
      id: 'a2LOt000006s0BJMAY',
      status: 'ExpiringSoon',
      accountNickname: null,
      sourceCode: null,
      isSavedForReuse: true,
      billingAddress: {
        attention: null,
        street: '1732 Gabriella Dr',
        city: 'Oxnard',
        stateCode: 'CA',
        postalCode: '93030-7667',
        country: 'United States',
      },
      paymentType: 'Credit Card',
      creditCard: {
        cardholderName: 'Charlie Fergison',
        cardType: 'Visa',
        digitalType: null,
        expirationDate: `${String(new Date(new Date().setMonth(new Date().getMonth() + 1)).getMonth() + 1).padStart(2, '0')}/${new Date(new Date().setMonth(new Date().getMonth() + 1)).getFullYear()}`,
        expiryStatus: 'EXPIRING_SOON',
        lastFour: '1111',
        responseCode: '1234',
        token: '1234',
      },
      ach: null,
      isCreatedForReceiptor: true,
    },
    {
      id: 'a2LOt000005s0BJMAY',
      status: 'Expired',
      accountNickname: null,
      sourceCode: null,
      isSavedForReuse: true,
      billingAddress: {
        attention: null,
        street: '422 Central Loop',
        city: 'Bakersfield',
        stateCode: 'CA',
        postalCode: '84222-7667',
        country: 'United States',
      },
      paymentType: 'Credit Card',
      creditCard: {
        cardholderName: 'Charlie Fergison',
        cardType: 'Mastercard',
        digitalType: null,
        expirationDate: `${String(new Date(new Date().setMonth(new Date().getMonth() - 1)).getMonth() + 1).padStart(2, '0')}/${new Date(new Date().setMonth(new Date().getMonth() - 1)).getFullYear()}`,
        expiryStatus: 'EXPIRED',
        lastFour: '5443',
        responseCode: '1234',
        token: '1234',
      },
      ach: null,
      isCreatedForReceiptor: true,
    },
  ],
  pledges: [
    {
      totalPledgeAmount: 361_816,
      totalGivenToDate: 223_973,
      status: 'Active',
      startDate: '2024-03-01',
      pledgeName: 'Beyond Measure',
      pledgeId: 'OPP-00021779',
      outstandingAmount: 137_843,
      nextInstallmentDate: '2026-05-31',
      nextInstallmentAmount: 137_843,
      lastInstallmentDate: '2025-05-19',
      lastInstallmentAmount: 112_341,
      endDate: '2026-06-30',
    },
  ],
  departureSummary: {
    departures: [
      {
        exitType: GraphQLDepartureExitType.Unplanned,
        effectiveDate: '2026-04-02',
        autoAcceptDate: '2026-05-07',
        departedChild: {
          globalId: '08314403',
          displayName: 'Luciana',
          imageUrl:
            'https://media.ci.org/w_150/v1536346920/ChildPhotos/Published/08314403_iskbag.jpg',
          commitmentInternalId: 'a1oPW00000wR5NDYA0',
        },
        sub: {
          pendingCommitmentId: 'a1ogP000006wfiUQAQ',
          completed: false,
          shouldDisplay: true,
          child: {
            globalId: '11752120',
            displayName: 'Samanta',
            imageUrl:
              'https://media.ci.org/w_150/v1745524128/ChildPhotos/Published/11752120_hvhgik.jpg',
            commitmentInternalId: 'a1ogP000006wfiUQAQ',
          },
        },
      },
    ],
    // // @ts-expect-error Temporary until AWSJSON typing is aligned with the actual departure summary shape.
    // subsRemaining: {
    //   '08314403': {
    //     acceptOfferedChild: true,
    //   },
    // },
  },
  contacts: MOCK_SUPPORTER_CONTACTS,
  primaryContactId: '0033b00002g6h35AAA',
  status: 'Active',
  type: 'Household',
  typeLevel2: 'Individual',
  typeLevel3: null,
  recordType: 'Individual',
  sourceCode: 'WEB',
  writingPreferences: ['DigitalPrint', 'Electronic'],
};

const unplannedDeparture =
  MOCK_SUPPORTER_MIXED_COMMITMENTS.departureSummary?.departures[0];

if (!unplannedDeparture) {
  throw new Error('The supporter mock requires a baseline departure.');
}

const plannedDeparture: GraphQLDeparture = {
  ...unplannedDeparture,
  exitType: GraphQLDepartureExitType.Planned,
  sub: null,
};

const plannedDepartureWithSub: GraphQLDeparture = {
  ...unplannedDeparture,
  exitType: GraphQLDepartureExitType.Planned,
};

const departedMockCommitment = MOCK_DEPARTURE_COMMITMENTS[0];
const pendingSubMockCommitment = MOCK_DEPARTURE_COMMITMENTS[1];

if (!departedMockCommitment || !pendingSubMockCommitment) {
  throw new Error('The supporter mock requires both departure commitments.');
}

const secondPendingSubCommitment: GraphQLCommitment = {
  ...pendingSubMockCommitment,
  globalId: 'CN-22430003',
  internalId: 'a1ogP000006wfiVQAQ',
  participant: {
    ...pendingSubMockCommitment.participant,
    birthdate: '2013-02-23',
    country: 'Peru',
    gender: 'Female',
    globalId: '09549200',
    localId: 'PE064400131',
    name: 'Erika',
    photoUrl:
      'https://media.ci.org/w_150/v1692289081/ChildPhotos/Published/09549200_gxuhss.jpg',
  },
};

const secondPlannedDeparture: GraphQLDeparture = {
  autoAcceptDate: '2026-06-12',
  effectiveDate: '2026-05-08',
  exitType: GraphQLDepartureExitType.Planned,
  departedChild: {
    commitmentInternalId: 'a1o3b000006CWBSAA4',
    displayName: 'Jona',
    globalId: '08741919',
    imageUrl:
      'https://media.ci.org/w_150/v1717045808/ChildPhotos/Published/08741919_fspnhs.jpg',
  },
  sub: null,
};

const secondPlannedDepartureWithSub: GraphQLDeparture = {
  ...secondPlannedDeparture,
  sub: {
    child: {
      commitmentInternalId: 'a1ogP000006wfiVQAQ',
      displayName: 'Erika',
      globalId: '09549200',
      imageUrl:
        'https://media.ci.org/w_150/v1692289081/ChildPhotos/Published/09549200_gxuhss.jpg',
    },
    completed: false,
    pendingCommitmentId: 'a1ogP000006wfiVQAQ',
    shouldDisplay: true,
  },
};

const createDepartureSupporterMock = (
  departures: GraphQLDeparture[],
  departureCommitments: GraphQLCommitment[] = [],
): GraphQLSupporter => ({
  ...MOCK_SUPPORTER_MIXED_COMMITMENTS,
  commitments: [
    ...(MOCK_SUPPORTER_MIXED_COMMITMENTS.commitments ?? []).filter(
      ({ internalId }) =>
        !MOCK_DEPARTURE_COMMITMENTS.some(
          (departureCommitment) =>
            departureCommitment.internalId === internalId,
        ),
    ),
    ...departureCommitments,
  ],
  departureSummary: { departures },
});

const plannedSubNoSameCenterCommitments = MOCK_DEPARTURE_COMMITMENTS.map(
  (commitment) =>
    commitment.participant?.globalId ===
    departedMockCommitment.participant?.globalId
      ? {
          ...commitment,
          participant: {
            ...commitment.participant,
            localId: 'ZZ000100001',
          },
        }
      : commitment,
);

export const MOCK_SUPPORTER_DEPARTURE_SCENARIOS = {
  none: createDepartureSupporterMock([]),
  unplanned: createDepartureSupporterMock(
    [unplannedDeparture],
    MOCK_DEPARTURE_COMMITMENTS,
  ),
  planned: createDepartureSupporterMock(
    [plannedDeparture],
    [departedMockCommitment],
  ),
  'planned-sub': createDepartureSupporterMock(
    [plannedDepartureWithSub],
    MOCK_DEPARTURE_COMMITMENTS,
  ),
  'planned-sub-no-same-center': createDepartureSupporterMock(
    [plannedDepartureWithSub],
    plannedSubNoSameCenterCommitments,
  ),
  'planned-sub-replacement-unavailable': createDepartureSupporterMock(
    [plannedDepartureWithSub],
    MOCK_DEPARTURE_COMMITMENTS,
  ),
  'multiple-departures': createDepartureSupporterMock(
    [plannedDeparture, secondPlannedDeparture],
    [departedMockCommitment],
  ),
  'multiple-subs': createDepartureSupporterMock(
    [plannedDepartureWithSub, secondPlannedDepartureWithSub],
    [...MOCK_DEPARTURE_COMMITMENTS, secondPendingSubCommitment],
  ),
  'hidden-sub': createDepartureSupporterMock(
    [
      {
        ...plannedDepartureWithSub,
        sub: plannedDepartureWithSub.sub
          ? { ...plannedDepartureWithSub.sub, shouldDisplay: false }
          : null,
      },
    ],
    [departedMockCommitment],
  ),
  'completed-sub': createDepartureSupporterMock(
    [
      {
        ...plannedDepartureWithSub,
        sub: plannedDepartureWithSub.sub
          ? { ...plannedDepartureWithSub.sub, completed: true }
          : null,
      },
    ],
    MOCK_DEPARTURE_COMMITMENTS,
  ),
  sparse: createDepartureSupporterMock(
    [
      {
        ...plannedDeparture,
        autoAcceptDate: null,
        departedChild: {
          ...plannedDeparture.departedChild,
          displayName: null,
          imageUrl: null,
        },
      },
    ],
    [departedMockCommitment],
  ),
} as const satisfies Record<string, GraphQLSupporter>;

export type MockSupporterDepartureScenario =
  keyof typeof MOCK_SUPPORTER_DEPARTURE_SCENARIOS;

const activeRecurringCommitments = (
  MOCK_SUPPORTER_MIXED_COMMITMENTS.commitments ?? []
).filter(
  (commitment): commitment is GraphQLCommitment =>
    !!commitment && commitment.status === 'Active' && !!commitment.isRecurring,
);

const [missedPaymentBaseCommitment, dueThisMonthBaseCommitment] =
  activeRecurringCommitments;

const createMakeAPaymentSupporterMock = ({
  commitments,
  givingTotals,
}: {
  commitments: GraphQLCommitment[];
  givingTotals: GraphQLGivingTotals;
}): GraphQLSupporter => {
  return {
    ...MOCK_SUPPORTER_MIXED_COMMITMENTS,
    commitments,
    givingTotals: {
      ...MOCK_SUPPORTER_MIXED_COMMITMENTS.givingTotals,
      ...givingTotals,
    },
  };
};

export const MOCK_SUPPORTER_WITH_MISSED_PAYMENT_COMMITMENTS =
  createMakeAPaymentSupporterMock({
    commitments: [
      {
        ...(missedPaymentBaseCommitment as GraphQLCommitment),
        hasMissedPayment: true,
        missedPaymentAmount: 48,
        hasDueThisMonth: false,
        dueThisMonthAmount: 0,
      },
      {
        ...(dueThisMonthBaseCommitment as GraphQLCommitment),
        hasMissedPayment: false,
        missedPaymentAmount: 0,
        hasDueThisMonth: true,
        dueThisMonthAmount: 24,
      },
    ],
    givingTotals: {
      totalMissedPaymentAmount: 48,
      totalDueThisMonthAmount: 24,
    },
  });

export const MOCK_SUPPORTER_WITH_DUE_THIS_MONTH_COMMITMENTS =
  createMakeAPaymentSupporterMock({
    commitments: [
      {
        ...(missedPaymentBaseCommitment as GraphQLCommitment),
        hasMissedPayment: false,
        missedPaymentAmount: 0,
        hasDueThisMonth: true,
        dueThisMonthAmount: 24,
      },
      {
        ...(dueThisMonthBaseCommitment as GraphQLCommitment),
        hasMissedPayment: false,
        missedPaymentAmount: 0,
        hasDueThisMonth: true,
        dueThisMonthAmount: 18,
      },
    ],
    givingTotals: {
      totalMissedPaymentAmount: 0,
      totalDueThisMonthAmount: 42,
    },
  });

export const MOCK_SUPPORTER_WITH_PAY_AHEAD_COMMITMENTS =
  createMakeAPaymentSupporterMock({
    commitments: [
      {
        ...(missedPaymentBaseCommitment as GraphQLCommitment),
        hasMissedPayment: false,
        missedPaymentAmount: 0,
        hasDueThisMonth: false,
        dueThisMonthAmount: 0,
      },
      {
        ...(dueThisMonthBaseCommitment as GraphQLCommitment),
        hasMissedPayment: false,
        missedPaymentAmount: 0,
        hasDueThisMonth: false,
        dueThisMonthAmount: 0,
      },
    ],
    givingTotals: {
      totalMissedPaymentAmount: 0,
      totalDueThisMonthAmount: 0,
    },
  });
